/**
 * The customer-facing quotation workbook — generate it, then hand it to the browser.
 *
 * ---- POST, never GET ----
 *
 * `quotation.create_quotation_excel_view` answers `200` to both verbs and returns a `File`
 * document either way, but only a POST actually persists it. Measured 2026-09-05:
 *
 *     GET  → 200 {name: "28da7b1e22", file_url: "/files/Quotation_…xlsx"}
 *            …then File/28da7b1e22 → 404, the File list is empty, and the url 404s.
 *     POST → 200 {name: "e0972d7ed2", file_url: "/files/Quotation_…xlsx"}
 *            …File e0972d7ed2 exists and the url returns 9,622 bytes of real `PK` xlsx.
 *
 * A GET builds the document, returns it, and throws it away. Calling this with the wrong verb
 * therefore yields a plausible-looking url that always 404s, which is worse than an error.
 *
 * ---- It fails on any quotation carrying a discount (`D31`) ----
 *
 * Controlled test on ONE quotation, changing only the discount:
 *
 *     additional_discount_percentage 0 → 200, workbook
 *                                    5 → 500 AttributeError: 'Quotation' object
 *                                            has no attribute 'apply_discount_on'
 *                                    0 → 200, workbook
 *
 * The field WAS `apply_additional_discount_on`; the generator read `apply_discount_on`, a name the
 * doctype did not then have. RESOLVED server-side by 2026-09-15: the doctype now carries
 * `apply_discount_on` (it persists, the old name is dropped), and a discounted quotation builds
 * its workbook (QTN-2026-00017 at 5% → 200, a File). The explanation below is kept for a bench
 * that has not taken that change; on this one it should never fire again.
 */

import { useMutation } from '@tanstack/react-query'
import { callPost } from './bosun'
import { fileHref } from './fileUrl'
import { withBosunError } from './useEnquiryMutations'
import type { BosunApiError } from './client'

export interface WireQuotationExcel {
  /** `File.name`. */
  name?: unknown
  file_name?: unknown
  /** Site-relative, e.g. `/files/Quotation_QTN-2026-00017….xlsx`. */
  file_url?: unknown
}

/** The `AttributeError` the server raises on a discounted quotation — see the module header. */
export const DISCOUNT_BUG_MARKER = 'apply_discount_on'

export function explainExcelFailure(message: string): string {
  return message.includes(DISCOUNT_BUG_MARKER)
    ? 'The server cannot build a workbook for a quotation that carries an additional discount ' +
        '(it reads a field the Quotation doctype does not have). Reported as D31 — until it is ' +
        'fixed, generating one means removing the discount first.'
    : `Could not build the quotation workbook: ${message}`
}

export function useQuotationExcel(options?: {
  onReady?: (fileUrl: string, fileName: string) => void
  onError?: (message: string) => void
}) {
  return useMutation<{ fileUrl: string; fileName: string }, BosunApiError, string>({
    mutationFn: withBosunError(async (quotation: string) => {
      const wire = await callPost<WireQuotationExcel>('quotation', 'create_quotation_excel_view', {
        quotation,
      })
      const fileUrl = typeof wire?.file_url === 'string' ? wire.file_url.trim() : ''
      if (fileUrl === '') throw new Error('the server returned no file_url')
      return {
        fileUrl,
        fileName: typeof wire?.file_name === 'string' && wire.file_name ? wire.file_name : `${quotation}.xlsx`,
      }
    }),
    /*
      Through `fileHref`, NOT the raw `/files/…` the server answers.

      Two reasons, both already established in that module: a site-relative path resolves against
      the APP's origin in a production build (`apiBase()` is Frappe's, and there is no proxy) —
      which is the Azure symptom, "opens a new tab with a different link". And nginx serves raw
      file paths without Frappe's CORS headers, so anything that ever fetches rather than
      navigates would break too. `download_file` also sets `Content-Disposition: attachment`,
      which names the saved file properly.
    */
    onSuccess: ({ fileUrl, fileName }) => {
      const href = fileHref(fileUrl)
      if (href) options?.onReady?.(href, fileName)
    },
    onError: (error) => options?.onError?.(explainExcelFailure(error.message)),
  })
}
